import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { Bid, BidStatus, Invoice, InvoiceStatus } from '../../types/models';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/formatters';

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-PY', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};


const getBidStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case BidStatus.ACTIVE:
      return 'primary';
    case BidStatus.ACCEPTED:
      return 'success';
    case BidStatus.REJECTED:
      return 'error';
    case BidStatus.EXPIRED:
      return 'warning';
    default:
      return 'default';
  }
};

const BidDetail: React.FC = () => {
  const { bidId } = useParams<{ bidId: string }>();
  const navigate = useNavigate();
  const { user, isSeller, isBuyer } = useAuth();
  const [bid, setBid] = useState<Bid | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchBidDetails = async () => {
      if (!bidId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch bid details - this will fail with 403 if user doesn't have permission
        const bidData = await apiService.get<Bid>(`/bids/${bidId}`);
        setBid(bidData);
        
        // If we got the bid successfully, fetch the related invoice
        try {
          const invoiceData = await apiService.get<Invoice>(`/invoices/${bidData.invoice_id}`);
          setInvoice(invoiceData);
          
          // Check if this is a bid on a sold invoice
          if (invoiceData.status === InvoiceStatus.SOLD && bidData.status !== BidStatus.ACCEPTED) {
            // This is not the winning bid on a sold invoice

          }
        } catch (invoiceErr: any) {
          console.error('Error fetching invoice details:', invoiceErr);
          // We still have the bid data, so don't set an error that would block the whole page
        }
      } catch (err: any) {
        // Check if it's a permission error (403)
        if (err.response?.status === 403) {
          setError('You do not have permission to view this bid. Only the bid owner (buyer) or the invoice seller can view bid details.');
        } else {
          const errorMessage = err.response?.data?.detail || 'Failed to load bid details';
          setError(errorMessage);
        }
        console.error('Error fetching bid details:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBidDetails();
  }, [bidId]);

  const handleAcceptBid = async () => {
    if (!bid) return;
    
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    
    try {
      await apiService.post(`/bids/${bid.id}/accept`, {});
      setActionSuccess('Bid accepted successfully. A transaction has been created.');
      
      // Refresh bid data to show updated status
      const updatedBid = await apiService.get<Bid>(`/bids/${bid.id}`);
      setBid(updatedBid);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to accept bid';
      setActionError(errorMessage);
      console.error('Error accepting bid:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectBid = async () => {
    if (!bid) return;
    
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    
    try {
      await apiService.post(`/bids/${bid.id}/reject`, {});
      setActionSuccess('Bid rejected successfully.');
      
      // Refresh bid data to show updated status
      const updatedBid = await apiService.get<Bid>(`/bids/${bid.id}`);
      setBid(updatedBid);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to reject bid';
      setActionError(errorMessage);
      console.error('Error rejecting bid:', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !bid) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Bid not found'}
        </Alert>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/bids')}
          >
            Return to My Bids
          </Button>
          {user && isBuyer && (
            <Button
              variant="contained"
              onClick={() => navigate('/marketplace')}
            >
              Go to Marketplace
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back
      </Button>

      <Typography variant="h4" gutterBottom>
        Bid Details
      </Typography>

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {actionError}
        </Alert>
      )}

      {actionSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {actionSuccess}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Bid Information</Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Chip 
                label={bid.status} 
                color={getBidStatusColor(bid.status)} 
                sx={{ mt: 0.5 }}
              />
              {invoice?.status === InvoiceStatus.SOLD && bid.status !== BidStatus.ACCEPTED && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  This invoice has been sold to another buyer
                </Typography>
              )}
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Bid Amount</Typography>
              <Typography variant="body1">{formatCurrency(bid.amount)}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Created Date</Typography>
              <Typography variant="body1">{formatDate(bid.created_at)}</Typography>
            </Box>
            {bid.notes && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Notes</Typography>
                <Typography variant="body1">{bid.notes}</Typography>
              </Box>
            )}
          </Grid>

          {invoice && (
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Invoice Information</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Invoice Number</Typography>
                <Typography variant="body1">{invoice.invoice_number}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Net Amount</Typography>
                <Typography variant="body1">{formatCurrency(invoice.net_amount, invoice.currency)}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Issue Date</Typography>
                <Typography variant="body1">{formatDate(invoice.issue_date)}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Due Date</Typography>
                <Typography variant="body1">{formatDate(invoice.due_date)}</Typography>
              </Box>
              <Button 
                variant="outlined" 
                onClick={() => navigate(`/invoices/${invoice.id}`)}
                sx={{ mt: 1 }}
              >
                View Invoice Details
              </Button>
            </Grid>
          )}
        </Grid>

        {isSeller && bid.status === BidStatus.ACTIVE && invoice?.status === InvoiceStatus.VERIFIED && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button 
                variant="outlined" 
                color="error" 
                onClick={handleRejectBid}
                disabled={actionLoading}
              >
                Reject Bid
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleAcceptBid}
                disabled={actionLoading}
              >
                Accept Bid
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {bid.status === BidStatus.ACCEPTED && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="success.main">
              Transaction Created
            </Typography>
            <Typography variant="body1">
              This bid has been accepted and a transaction has been created.
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/transactions')}
              sx={{ mt: 2 }}
            >
              View Transactions
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BidDetail;
